/* eslint-disable max-lines -- not split yet: body-format helpers and response interceptors could leave the NoddiAsync class */
import * as _ReactQuery from '@tanstack/react-query';
import { InfiniteData, UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { NoddiAsyncError } from 'noddi-types';
import { buildGetQueryOptions } from './buildGetQueryOptions';
import { convertObjectKeysToCamelCase, convertObjectKeysToSnakeCase } from './keyConversion';
import { mergeSideEffectGetQueryConfig } from './mergeSideEffectGetQueryConfig';
import { normalizeBlobError } from './normalizeBlobError';
import {
  getRateLimitRetryDelayMs,
  getRetryAfterHeader,
  type RateLimitRetryConfig,
  shouldRetryRateLimit
} from './rateLimitRetry';
import { getUrls, InputData, NoddiAsyncReturnType, OutputData, URLKeysTypes } from './resources/resources';

type ReactQuery = typeof _ReactQuery;

class NoddiAsync {
  // @ts-expect-error no initializer
  public queryClient: _ReactQuery.QueryClient;
  // @ts-expect-error no initializer
  private reactQuery: ReactQuery;
  // @ts-expect-error no initializer
  private baseUrl: string;
  // @ts-expect-error no initializer
  private authToken: string;
  // @ts-expect-error no initializer
  private impersonatedAuthToken: string;
  // @ts-expect-error no initializer
  private axiosInstance: AxiosInstance;

  init({
    reactQuery,
    baseUrl,
    refetchOnWindowFocus = false,
    queryCacheConfig,
    mutationCacheConfig,
    source,
    sourceVersion,
    sourceReleasedAt,
    sdkVersion
  }: {
    reactQuery: ReactQuery;
    baseUrl: string;
    refetchOnWindowFocus?: boolean;
    queryCacheConfig?: _ReactQuery.QueryCache['config'];
    mutationCacheConfig?: _ReactQuery.MutationCache['config'];
    source?: string;
    sourceVersion?: string;
    /** ISO timestamp of when this build was released (`__SOURCE_RELEASED_AT__`). */
    sourceReleasedAt?: string;
    /** Installed `@navio/nsdk` version — the API contract this build was generated against. */
    sdkVersion?: string;
  }) {
    this.queryClient = new reactQuery.QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus,
          retry: false
        }
      },

      queryCache: queryCacheConfig ? new reactQuery.QueryCache(queryCacheConfig) : undefined,
      mutationCache: mutationCacheConfig ? new reactQuery.MutationCache(mutationCacheConfig) : undefined
    });

    // The backend translates labels and errors by Accept-Language, which the device or browser would otherwise set.
    // English matches every app but the customer app, which sends its own locale.
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Accept-Language': 'en' };
    if (source) {
      headers['X-Navio-Source'] = source;
    }
    if (sourceVersion) {
      headers['X-Navio-Source-Version'] = sourceVersion;
    }
    if (sourceReleasedAt) {
      headers['X-Navio-Source-Updated-At'] = sourceReleasedAt;
    }
    if (sdkVersion) {
      headers['X-Navio-Sdk-Version'] = sdkVersion;
    }

    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers,
      withCredentials: false
    });
    attachResponseErrorInterceptor(this.axiosInstance);
    this.reactQuery = reactQuery;
    this.baseUrl = baseUrl;
  }

  getReactQuery() {
    return this.reactQuery;
  }

  getAxiosInstance() {
    return this.axiosInstance;
  }

  public setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Requests use the axios instance. Updating only `this.baseUrl` left every call on the URL from init.
    if (this.axiosInstance) {
      this.axiosInstance.defaults.baseURL = baseUrl;
    }
  }

  /**
   * Set (or clear) a default request header sent on every subsequent request.
   * Use for dynamic `X-Navio-*` context headers whose value changes at runtime —
   * e.g. `X-Navio-Brand` (customer app) and `X-Navio-Tenant` (partner app, the
   * service department id) — unlike
   * the static `X-Navio-Source*` headers fixed at `init`. Passing a nullish/empty
   * value removes the header. Backend CORS must allow-list any custom header.
   */
  public setHeader(name: string, value?: string | null) {
    const common = this.axiosInstance.defaults.headers.common;
    if (value === undefined || value === null || value === '') {
      delete common[name];
    } else {
      common[name] = value;
    }
  }

  public getBaseUrl() {
    return this.baseUrl;
  }

  public setAuthToken(authToken: string) {
    this.authToken = authToken;
  }

  public getAuthToken() {
    return this.authToken;
  }

  public setImpersonatedAuthToken(impersonatedAuthToken: string) {
    this.impersonatedAuthToken = impersonatedAuthToken;
  }

  public getImpersonatedAuthToken() {
    return this.impersonatedAuthToken;
  }

  /** Returns the active token for the current session, preferring impersonated token when active. */
  public getActiveAuthToken() {
    const impersonated = this.getImpersonatedAuthToken();
    if (impersonated) {
      return impersonated;
    }
    return this.getAuthToken();
  }

  public getAuthHeaders({ ignoreImpersonation = false }: { ignoreImpersonation?: boolean }) {
    const token = ignoreImpersonation ? this.getAuthToken() : this.getActiveAuthToken();

    return token ? { Authorization: `Token ${token}` } : {};
  }

  NoddiServerContext = ({ children }: { children: React.ReactNode }) => (
    <this.reactQuery.QueryClientProvider client={this.queryClient}>{children}</this.reactQuery.QueryClientProvider>
  );

  /**
   * Run a GET resource outside React Query (same request pipeline as `useGet` / `downloadFile`).
   * Prefer `useDownload` in components so loading/error state is consistent with other mutations.
   */
  private executeGet<UrlKey extends URLKeysTypes>(
    type: UrlKey,
    input: InputDataOrVoid<UrlKey>
  ): Promise<OutputData<UrlKey>> {
    const {
      getUrl,
      getHeaders,
      ignoreImpersonation,
      skipConvertingToCamelCase,
      handleRes,
      responseType = 'json'
    } = getUrls[type];

    const authHeaders = this.getAuthHeaders({ ignoreImpersonation });
    const resolvedInput = input;
    const url = getUrl(resolvedInput);
    const headers = getHeaders?.(resolvedInput) ?? {};

    return this.axiosInstance
      .get<OutputData<UrlKey>>(url, {
        noddiEndpoint: type,
        headers: { ...authHeaders, ...headers },
        responseType
      })
      .then((res) => {
        if (handleRes) {
          return handleRes(res);
        }
        return convertToCorrectFrontendFormat({ res, skipConvertingToCamelCase });
      }) as Promise<OutputData<UrlKey>>;
  }

  /**
   * Imperative GET for side-effect resources (e.g. browser file download). Does not touch the query cache.
   */
  downloadFile<
    UrlKey extends URLKeysTypes,
    Metadata extends InputData<UrlKey>,
    SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
  >(opts: Omit<GetProps<UrlKey, Metadata, SelectedData>, 'queryConfig'>): Promise<OutputData<UrlKey>> {
    const { type, input } = opts;
    return this.executeGet(type, input as InputDataOrVoid<UrlKey>);
  }

  /**
   * Mutation wrapper for `downloadFile` — same semantics as `usePost`’s `queryConfig` for mutation options.
   */
  useDownload<UrlKey extends URLKeysTypes>({
    type,
    queryConfig
  }: {
    type: UrlKey;
    queryConfig?: Omit<
      _ReactQuery.UseMutationOptions<OutputData<UrlKey>, NoddiAsyncError, InputDataOrVoid<UrlKey>, unknown>,
      'mutationFn'
    >;
  }): _ReactQuery.UseMutationResult<OutputData<UrlKey>, NoddiAsyncError, InputDataOrVoid<UrlKey>, unknown> {
    return this.reactQuery.useMutation({
      mutationFn: (inputVariable) => this.executeGet(type, inputVariable),
      ...queryConfig
    });
  }

  prefetch<UrlKey extends URLKeysTypes>({ type, input }: { type: UrlKey; input: InputDataOrVoid<UrlKey> }) {
    return this.queryClient.prefetchQuery({
      queryKey: (input !== undefined ? [type, input] : [type]) as [UrlKey, ...unknown[]],
      queryFn: () => this.executeGet(type, input)
    });
  }

  /** Same cache key as `useGet`. A click can read the record before it navigates. */
  fetch<UrlKey extends URLKeysTypes>({
    type,
    input
  }: {
    type: UrlKey;
    input: InputDataOrVoid<UrlKey>;
  }): Promise<OutputData<UrlKey>> {
    return this.queryClient.fetchQuery({
      queryKey: (input !== undefined ? [type, input] : [type]) as [UrlKey, ...unknown[]],
      queryFn: () => this.executeGet(type, input)
    });
  }

  useGet<
    UrlKey extends URLKeysTypes,
    Metadata extends InputData<UrlKey>,
    SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
  >({ type, queryConfig = {}, input }: GetProps<UrlKey, Metadata, SelectedData>) {
    const data = this.reactQuery.useQuery(
      buildGetQueryOptions<NoddiAsyncReturnType<UrlKey, Metadata>, SelectedData>({
        type,
        input,
        runRequest: () =>
          this.executeGet(type, input as InputDataOrVoid<UrlKey>) as Promise<NoddiAsyncReturnType<UrlKey, Metadata>>,
        queryConfig
      })
    );

    return data;
  }

  /**
   * Infinite-loading variant of `useGet` for paginated endpoints that return `PaginatedResponse`
   * (`{ next, pageIndex, pageSize, count, results }`). The hook drives `pageIndex` internally via
   * `pageParam`, so callers omit it from `input`. All other fields (`pageSize`, `search`, filters,
   * `order`) stay in `input` — changing any of them produces a new query key and a fresh page-0
   * fetch.
   *
   * Returns the raw `useInfiniteQuery` result. Flatten in the consumer:
   * `data?.pages.flatMap(p => p.results) ?? []`.
   */
  useInfiniteGet<UrlKey extends URLKeysTypes, Metadata extends InputData<UrlKey>>({
    type,
    input,
    queryConfig
  }: InfiniteGetProps<UrlKey, Metadata>) {
    const { sideEffectGet } = getUrls[type];

    return this.reactQuery.useInfiniteQuery<
      NoddiAsyncReturnType<UrlKey, Metadata>,
      NoddiAsyncError,
      InfiniteData<NoddiAsyncReturnType<UrlKey, Metadata>, number>,
      [UrlKey, Omit<Metadata, 'pageIndex'>],
      number
    >({
      queryKey: [type, input],
      queryFn: ({ pageParam }) =>
        this.executeGet(type, { ...input, pageIndex: pageParam } as unknown as InputDataOrVoid<UrlKey>),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        const page = lastPage as unknown as { next?: string | null; pageIndex?: number };
        if (!page?.next || typeof page.pageIndex !== 'number') {
          return undefined;
        }
        return page.pageIndex + 1;
      },
      ...mergeSideEffectGetQueryConfig(sideEffectGet, (queryConfig ?? {}) as Record<string, unknown>)
    });
  }

  /**
   * useGetAll is a custom hook that wraps the useQueries function from react-query.
   * It allows you to perform multiple GET requests concurrently.
   *
   * now the types only support one type of query, however, the code actually supports different kinds of queries
   *
   * @template UrlKey - A type that extends URLKeysTypes. It represents the key for the URL of the request.
   * @template Metadata - A type that extends InputData<UrlKey>. It represents the metadata for the request.
   * @template SelectedData - The type of the data returned by the request. By default, it's NoddiAsyncReturnType<UrlKey, Metadata>.
   *
   * @param {Array<GetProps<UrlKey, Metadata, SelectedData>>} queriesInputs - An array of objects, where each object represents a query.
   * Each object must have a 'type' property (which corresponds to the UrlKey), and can optionally have 'queryConfig' and 'input' properties.
   * 'queryConfig' is an object of additional configuration options for the query.
   * 'input' is the metadata for the request.
   *
   *
   * @returns {Array<UseQueryResult>} An array of query results. Each result is an object with properties like 'isLoading', 'isError', 'data', and 'error'.
   */
  useGetAll<
    UrlKey extends URLKeysTypes,
    Metadata extends InputData<UrlKey>,
    SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
  >(queriesInputs: Array<GetProps<UrlKey, Metadata, SelectedData>>) {
    const queries = queriesInputs.map(({ type, queryConfig = {}, input }) =>
      buildGetQueryOptions<NoddiAsyncReturnType<UrlKey, Metadata>, SelectedData>({
        type,
        input,
        runRequest: () => this.executeGet(type, input as InputDataOrVoid<UrlKey>),
        queryConfig
      })
    );

    return this.reactQuery.useQueries({ queries });
  }

  usePost<Type extends URLKeysTypes>({ type, queryConfig }: PostProps<Type>): ReturnTypeMutation<Type> {
    if (!queryConfig) {
      queryConfig = {};
    }
    return this.reactQuery.useMutation({
      mutationFn: (input) => {
        const {
          bodyWithSnakeCaseFormat,
          url,
          responseType,
          handleRes,
          getHeaders,
          body,
          skipBodyTransformation,
          ignoreImpersonation,
          skipConvertingToCamelCase
        } = prepareMutation({
          type,
          input
        });
        const _responseType = responseType ? { responseType } : undefined;
        const authHeaders = this.getAuthHeaders({ ignoreImpersonation });
        const headers = getHeaders?.(input) ?? {};

        return this.axiosInstance
          .post<OutputData<Type>>(url, skipBodyTransformation ? body : bodyWithSnakeCaseFormat, {
            noddiEndpoint: type,
            ..._responseType,
            headers: { ...authHeaders, ...headers }
          })
          .then((res) => {
            if (handleRes) {
              return handleRes(res);
            }
            // @ts-expect-error convertObjectKeysToCamelCase widens the result past OutputData<Type>
            return skipConvertingToCamelCase ? res : convertObjectKeysToCamelCase(res);
          });
      },
      ...queryConfig
    });
  }
  // This allows us to use a POST request as a GET request
  // Useful in cases we want to fetch data with complex params, like [{ carId: 1 , salesItemIds : [1,2,3] }, { carId: 2 , salesItemIds : [3] }]
  // Then we put the input in the body of the request, instead of the query params
  usePostAsGetQuery<
    UrlKey extends URLKeysTypes,
    Metadata extends InputData<UrlKey>,
    SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
  >(props: GetProps<UrlKey, Metadata, SelectedData>) {
    return noddiAsync
      .getReactQuery()
      .useQuery<NoddiAsyncReturnType<UrlKey, Metadata>, NoddiAsyncError, SelectedData>(postAsGetQueryOptions(props));
  }

  /** usePostAsGetQuery for a list of inputs, as useGetAll is for useGet. */
  usePostAsGetQueries<
    UrlKey extends URLKeysTypes,
    Metadata extends InputData<UrlKey>,
    SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
  >(queriesInputs: Array<GetProps<UrlKey, Metadata, SelectedData>>) {
    return noddiAsync
      .getReactQuery()
      .useQueries({ queries: queriesInputs.map((props) => postAsGetQueryOptions(props)) });
  }

  usePatch<Type extends URLKeysTypes>({ type, queryConfig }: PostProps<Type>): ReturnTypeMutation<Type> {
    if (!queryConfig) {
      queryConfig = {};
    }

    const data = this.reactQuery.useMutation({
      mutationFn: (input) => {
        const {
          skipBodyTransformation,
          bodyWithSnakeCaseFormat,
          url,
          ignoreImpersonation,
          body,
          skipConvertingToCamelCase,
          getHeaders
        } = prepareMutation({
          type,
          input
        });
        const authHeaders = this.getAuthHeaders({ ignoreImpersonation });
        const headers = getHeaders?.(input) ?? {};

        return (
          this.axiosInstance
            .patch<OutputData<Type>>(url, skipBodyTransformation ? body : bodyWithSnakeCaseFormat, {
              noddiEndpoint: type,
              headers: { ...authHeaders, ...headers }
            })
            // @ts-expect-error convertObjectKeysToCamelCase widens the result past OutputData<Type>
            .then((res) => (skipConvertingToCamelCase ? res : convertObjectKeysToCamelCase(res))) as Promise<
            OutputData<Type>
          >
        );
      },
      ...queryConfig
    });

    return data;
  }

  useDelete<Type extends URLKeysTypes>({ type, queryConfig }: PostProps<Type>): ReturnTypeMutation<Type> {
    if (!queryConfig) {
      queryConfig = {};
    }

    const data = this.reactQuery.useMutation({
      mutationFn: (input) => {
        const {
          url,
          ignoreImpersonation,
          skipConvertingToCamelCase,
          body,
          bodyWithSnakeCaseFormat,
          skipBodyTransformation,
          getHeaders
        } = prepareMutation({ type, input });
        const authHeaders = this.getAuthHeaders({ ignoreImpersonation });
        const headers = getHeaders?.(input) ?? {};
        // Most deletes identify the record via the URL and send no body; forward a
        // body only when the resource defines one (e.g. DELETE with a session_id payload).
        const requestBody = skipBodyTransformation ? body : bodyWithSnakeCaseFormat;
        const hasBody = requestBody && Object.keys(requestBody).length > 0;

        return (
          this.axiosInstance
            .delete<OutputData<Type>>(url, {
              noddiEndpoint: type,
              headers: { ...authHeaders, ...headers },
              ...(hasBody ? { data: requestBody } : {})
            })
            // @ts-expect-error convertObjectKeysToCamelCase widens the result past OutputData<Type>
            .then((res) => (skipConvertingToCamelCase ? res : convertObjectKeysToCamelCase(res))) as Promise<
            OutputData<Type>
          >
        );
      },
      ...queryConfig
    });

    return data;
  }
}

function prepareMutation<Type extends URLKeysTypes>({ type, input }: { type: Type; input: InputDataOrVoid<Type> }) {
  const {
    getBody,
    getUrl,
    responseType,
    handleRes,
    getHeaders,
    ignoreImpersonation,
    skipConvertingToCamelCase,
    skipBodyTransformation
  } = getUrls[type];
  const body = getBody?.(input) || {};
  const bodyWithSnakeCaseFormat = skipBodyTransformation ? {} : convertBodyToCorrectFormat({ body });
  const url = getUrl(input);

  return {
    bodyWithSnakeCaseFormat,
    url,
    responseType,
    handleRes,
    getHeaders,
    ignoreImpersonation,
    body,
    skipConvertingToCamelCase,
    skipBodyTransformation
  };
}

function convertToCorrectFrontendFormat({
  res,
  skipConvertingToCamelCase
}: {
  res: AxiosResponse;
  skipConvertingToCamelCase?: boolean;
}) {
  // 204 indicates no content from server
  if (res.status === 204) {
    return null;
  }

  return skipConvertingToCamelCase ? res.data : convertObjectKeysToCamelCase(res.data);
}

type Body = Record<string | number, unknown> | Record<string | number, unknown>[];

function convertBodyToCorrectFormat({ body }: { body: Body }) {
  return convertObjectKeysToSnakeCase(body) as AxiosRequestConfig<Body>;
}

function attachResponseErrorInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(rejectStatusZeroResponse, async (error: unknown) => {
    await normalizeBlobError(error);
    const axiosError = error as {
      response?: { status?: number; headers?: Record<string, string> };
      config?: AxiosRequestConfig & RateLimitRetryConfig;
    };
    const config = axiosError.config;
    const retryCount = config?.rateLimitRetryCount ?? 0;
    if (
      !config ||
      !shouldRetryRateLimit({
        status: axiosError.response?.status,
        retryCount
      })
    ) {
      // oxlint-disable-next-line typescript/prefer-promise-reject-errors -- the interceptor re-rejects its own caught error untouched; wrapping it would hide the AxiosError every caller inspects
      return Promise.reject(error);
    }

    config.rateLimitRetryCount = retryCount + 1;
    const delayMs = getRateLimitRetryDelayMs({
      retryAfterHeader: getRetryAfterHeader(axiosError.response?.headers),
      attempt: retryCount
    });
    await new Promise((resolve) => {
      setTimeout(resolve, delayMs);
    });
    return instance.request(config);
  });
}

// axios settles a status-0 XHR as a success (`settle` skips a falsy status), so a
// request the browser tore down mid-navigation resolves with `''` where the
// caller expects a record. Reject it as the no-response failure it is, which
// `getApiErrorSeverity` already treats as offline noise.
function rejectStatusZeroResponse(response: AxiosResponse): AxiosResponse | Promise<never> {
  if (response.status !== 0) {
    return response;
  }
  return Promise.reject(new AxiosError('Network Error', AxiosError.ERR_NETWORK, response.config, response.request));
}

interface PostProps<Type extends URLKeysTypes> {
  type: Type;
  queryConfig?: Omit<
    _ReactQuery.UseMutationOptions<OutputData<Type>, NoddiAsyncError, InputDataOrVoid<Type>, unknown>,
    'mutationFn'
  >;
}

type ReturnTypeMutation<Type extends URLKeysTypes> = _ReactQuery.UseMutationResult<
  OutputData<Type>,
  NoddiAsyncError,
  InputDataOrVoid<Type>,
  unknown
>;

export type GetProps<Type extends URLKeysTypes, QueryParams, SelectedData> = {
  type: Type;
  queryConfig?: Omit<
    UseQueryOptions<NoddiAsyncReturnType<Type, QueryParams>, NoddiAsyncError, SelectedData>,
    'queryKey' | 'queryFn'
  >;
} & (InputData<Type> extends void
  ? { input?: void }
  : Partial<InputData<Type>> extends InputData<Type>
    ? { input?: InputData<Type> }
    : { input: InputData<Type> });

type InfiniteGetProps<Type extends URLKeysTypes, QueryParams extends InputData<Type>> = {
  type: Type;
  input: Omit<QueryParams, 'pageIndex'>;
  queryConfig?: Omit<
    UseInfiniteQueryOptions<
      NoddiAsyncReturnType<Type, QueryParams>,
      NoddiAsyncError,
      InfiniteData<NoddiAsyncReturnType<Type, QueryParams>, number>,
      [Type, Omit<QueryParams, 'pageIndex'>],
      number
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >;
};

/** What `useGet` returns. */
export type GetResult<SelectedData> = _ReactQuery.UseQueryResult<SelectedData, NoddiAsyncError>;

export type InputDataOrVoid<Type extends URLKeysTypes> = InputData<Type> extends void ? void : InputData<Type>;

const noddiAsync = new NoddiAsync();

export default noddiAsync;

const postAsGetQueryOptions = <
  UrlKey extends URLKeysTypes,
  Metadata extends InputData<UrlKey>,
  SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
>({
  type,
  queryConfig = {},
  input
}: GetProps<UrlKey, Metadata, SelectedData>) => ({
  queryKey: (input !== undefined ? [type, input] : [type]) as [UrlKey, ...unknown[]],
  queryFn: (): Promise<NoddiAsyncReturnType<UrlKey, Metadata>> => {
    const resolvedInput = input as InputDataOrVoid<UrlKey>;
    const {
      bodyWithSnakeCaseFormat,
      url,
      responseType,
      getHeaders,
      body,
      skipBodyTransformation,
      ignoreImpersonation,
      skipConvertingToCamelCase
    } = prepareMutation({
      type,
      input: resolvedInput
    });

    const _responseType = responseType ? { responseType } : undefined;
    const authHeaders = noddiAsync.getAuthHeaders({ ignoreImpersonation });
    const headers = getHeaders?.(resolvedInput) ?? {};

    return (
      noddiAsync
        .getAxiosInstance()
        .post<OutputData<UrlKey>>(url, skipBodyTransformation ? body : bodyWithSnakeCaseFormat, {
          noddiEndpoint: type,
          ..._responseType,
          headers: { ...authHeaders, ...headers }
        })
        // @ts-expect-error convertObjectKeysToCamelCase widens the result past OutputData<UrlKey>
        .then((res) => (skipConvertingToCamelCase ? res : convertObjectKeysToCamelCase(res))) as Promise<
        NoddiAsyncReturnType<UrlKey, Metadata>
      >
    );
  },
  ...queryConfig
});
